import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import type { TripStore } from "../hooks/useTripData";
import { scoreVotes } from "../data/migrateTrip";
import { VoteButtons } from "./VoteButtons";
import { PlaceDetail } from "./PlaceDetail";
import { pinMayHavePhoto } from "../lib/googlePlaces";
import { CategoryIcon, StatusBadge } from "./StatusBadge";
import { Icon } from "./Icon";
import type { AttractionIdea, IdeaRegion, TravelerId } from "../types";

const REGIONS: Array<IdeaRegion | "All"> = [
  "All",
  "Santiago",
  "Atacama",
  "Puerto Natales",
  "Patagonia",
];

interface Props {
  store: TripStore;
  selectedDate: string;
  onSelectDate: (date: string) => void;
  voterId: TravelerId;
}

function IdeaCard({
  idea,
  store,
  selectedDate,
  onSelectDate,
  voterId,
}: {
  idea: AttractionIdea;
  store: TripStore;
  selectedDate: string;
  onSelectDate: (date: string) => void;
  voterId: TravelerId;
}) {
  const { data } = store;
  const dayOptions = idea.suggestedDays?.length
    ? idea.suggestedDays
    : data.days.map((d) => d.date);
  const [targetDay, setTargetDay] = useState(
    idea.suggestedDays?.[0] ?? selectedDate,
  );
  const pin = idea.pinId ? data.pins.find((p) => p.id === idea.pinId) : undefined;
  const score = scoreVotes(data.votes[idea.id]);

  return (
    <article className={`idea-card${score > 0 ? " is-winner" : ""}${idea.category === "coffee" ? " is-coffee" : ""}`}>
      <div className="idea-card-head">
        <div className="idea-card-top">
          <CategoryIcon category={idea.category} />
          <div>
            <p className="eyebrow">{idea.region}</p>
            <h4>{idea.name}</h4>
            {idea.detail && <p>{idea.detail}</p>}
            <StatusBadge status="proposed" />
          </div>
        </div>
        <VoteButtons
          voterId={voterId}
          targetId={idea.id}
          tally={data.votes[idea.id]}
          onVote={store.setVote}
        />
      </div>
      {pin && pinMayHavePhoto(pin) && <PlaceDetail pin={pin} compact />}
      <div className="idea-actions">
        <label>
          Add to day
          <select value={targetDay} onChange={(e) => setTargetDay(e.target.value)}>
            {dayOptions.map((date) => {
              const day = data.days.find((d) => d.date === date);
              return (
                <option key={date} value={date}>
                  {format(parseISO(date), "d MMM")}
                  {day ? ` · ${day.title}` : ""}
                </option>
              );
            })}
          </select>
        </label>
        <button
          type="button"
          className="btn"
          onClick={() => {
            store.addIdeaToDay(idea.id, targetDay);
            onSelectDate(targetDay);
          }}
        >
          <Icon name="plus" size={15} /> Add to schedule
        </button>
      </div>
    </article>
  );
}

export function IdeasPanel({ store, selectedDate, onSelectDate, voterId }: Props) {
  const { data } = store;
  const [region, setRegion] = useState<IdeaRegion | "All">("All");
  const [winnersOnly, setWinnersOnly] = useState(false);

  const ideas = useMemo(() => {
    let list = [...data.ideas];
    if (region !== "All") list = list.filter((i) => i.region === region);
    list.sort(
      (a, b) => scoreVotes(data.votes[b.id]) - scoreVotes(data.votes[a.id]),
    );
    if (winnersOnly) list = list.filter((i) => scoreVotes(data.votes[i.id]) > 0);
    return list;
  }, [data.ideas, data.votes, region, winnersOnly]);

  return (
    <div className="ideas-panel">
      <div className="ideas-toolbar">
        <div className="region-filters" aria-label="Filter ideas by region">
          {REGIONS.map((r) => (
            <button
              key={r}
              type="button"
              className={region === r ? "selected" : ""}
              aria-pressed={region === r}
              onClick={() => setRegion(r)}
            >
              {r}
            </button>
          ))}
        </div>
        <label className="winners-toggle">
          <input
            type="checkbox"
            checked={winnersOnly}
            onChange={(e) => setWinnersOnly(e.target.checked)}
          />
          Show winners only (net 👍)
        </label>
      </div>

      <p className="ideas-hint">
        Vote 👍 or 👎. Add a winner to a day, then
        reorder in Itinerary with drag handles (long-press on mobile) or ↑↓.
      </p>

      <div className="idea-cards">
        {ideas.map((idea) => (
          <IdeaCard
            key={idea.id}
            idea={idea}
            store={store}
            selectedDate={selectedDate}
            onSelectDate={onSelectDate}
            voterId={voterId}
          />
        ))}
        {ideas.length === 0 && (
          <div className="empty-state">
            <Icon name="check" size={28} />
            <h4>No ideas match</h4>
            <p>Clear the winners filter or pick another region.</p>
          </div>
        )}
      </div>
    </div>
  );
}
